// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * MSAL Configuration for Azure Entra External ID (CIAM)
 *
 * This module configures the MSAL.js PublicClientApplication used for
 * authentication via Azure Entra External ID.  The singleton `msalInstance`
 * is shared between the React <MsalProvider> and non-React service modules
 * (authService, api).
 *
 * Environment variables (set in .env / CI):
 *   REACT_APP_CIAM_CLIENT_ID   – App registration client ID
 *   REACT_APP_CIAM_TENANT_ID   – Entra External ID tenant ID
 *   REACT_APP_CIAM_DOMAIN      – CIAM login host. Use the tenant subdomain
 *                                (heliosexternaldev.ciamlogin.com), not the
 *                                tenant-id host: federated sign-in copies this
 *                                host into the workforce app's redirect URI.
 *   REACT_APP_APIM_SCOPE       – APIM API scope (e.g. api://helios-dev-apim-oauth-external/.default)
 */

import {
  type Configuration,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

const clientId = process.env.REACT_APP_CIAM_CLIENT_ID ?? '';
const tenantId = process.env.REACT_APP_CIAM_TENANT_ID ?? '';
const domain = process.env.REACT_APP_CIAM_DOMAIN ?? '';
const bffScope = process.env.REACT_APP_BFF_SCOPE ?? '';
const apimScope = process.env.REACT_APP_APIM_SCOPE ?? '';

if (process.env.NODE_ENV === 'development') {
  console.group('[Helios] Environment');
  console.log('REACT_APP_API_URL        ', process.env.REACT_APP_API_URL);
  console.log('REACT_APP_CIAM_CLIENT_ID ', clientId);
  console.log('REACT_APP_CIAM_TENANT_ID ', tenantId);
  console.log('REACT_APP_CIAM_DOMAIN    ', domain);
  console.log('REACT_APP_BFF_SCOPE      ', bffScope);
  console.log('REACT_APP_APIM_SCOPE     ', apimScope);
  console.log('NODE_ENV                 ', process.env.NODE_ENV);
  console.log('Authority                ', `https://${domain}/${tenantId}`);
  console.groupEnd();
}

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://${domain}/${tenantId}`,
    knownAuthorities: [domain],
    redirectUri: window.location.origin,
    postLogoutRedirectUri: `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning, // LogLevel.Verbose
      loggerCallback: (_level, message, containsPii) => {
        if (!containsPii) {
          console.debug(`[MSAL] ${message}`);
        }
      },
    },
  },
};

/**
 * Scopes requested during login and silent token acquisition.
 * `openid`, `profile`, and `email` are implicit with MSAL but listed
 * explicitly for clarity.  The `access_as_user` scope grants the access
 * token audience expected by downstream APIs.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', bffScope],
};

/**
 * Singleton MSAL instance.
 * Created at module load time so it's available before React renders.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Acquire an access token silently from MSAL's cache or via a refresh.
 * If silent acquisition fails because user interaction is required,
 * redirect to CIAM login.
 *
 * This function lives in authConfig (not authService) to avoid a circular
 * dependency: api.ts needs the token, and authService.ts imports from api.ts.
 *
 * @returns The access token string.
 * @throws  If no active account exists or interaction is required.
 */
export const getTokenAsync = async (): Promise<string> => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error('No active MSAL account');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Silent refresh failed — redirect to CIAM for interactive login
      // Return to the page the user is on. ProtectedRoute no longer detours
      // through /login during startup, so this is the real page.
      await msalInstance.loginRedirect({
        ...loginRequest,
        redirectStartPage: window.location.href,
      });
      // The redirect navigates away; this line is unreachable.
      throw error;
    }
    throw error;
  }
};
